import { expectArray, expectBoolean, expectRecord, expectString } from "./schema";
import { parseStudentBillingCurrency, parseStudentBillingDate, parseStudentBillingIdentifier, parseStudentBillingServiceMonth } from "./student-billing";
import { STUDENT_BILLING_COLLECTION_METHODS, STUDENT_BILLING_COLLECTION_SOURCES } from "./student-billing-collection-authority";
import { parseStudentBillingMonthlyLine, parseStudentBillingMonthlyUnit } from "./student-billing-monthly-draft";
export const STUDENT_BILLING_TENANT_COLLECTION_STATUSES = ["notIssued", "issued", "collected"];
function exact(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function integer(value, path, minimum = 0) {
    if (!Number.isSafeInteger(value) || Number(value) < minimum)
        throw new Error(`${path} is invalid`);
    return Number(value);
}
function enumValue(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function iso(value, path) {
    const parsed = expectString(value, path);
    if (!Number.isFinite(new Date(parsed).getTime()))
        throw new Error(`${path} is invalid`);
    return new Date(parsed).toISOString();
}
function fingerprint(value, path) {
    const parsed = expectString(value, path);
    if (!/^[a-f0-9]{64}$/u.test(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
export function parseFinalizedStudentBillingCardFeePolicySnapshot(value, path = "finalizedCardFeePolicySnapshot") {
    const record = expectRecord(value, path);
    exact(record, [
        "platformFeePolicyKey", "pricingAssignmentId", "feeRateBps", "applicationFeeWaived",
        "commercialPolicyVersion", "pricingCatalogVersion"
    ], path);
    const feeRateBps = integer(record.feeRateBps, `${path}.feeRateBps`, 1);
    if (feeRateBps > 10_000)
        throw new Error(`${path}.feeRateBps is invalid`);
    return {
        platformFeePolicyKey: fingerprint(record.platformFeePolicyKey, `${path}.platformFeePolicyKey`),
        pricingAssignmentId: parseStudentBillingIdentifier(record.pricingAssignmentId, `${path}.pricingAssignmentId`),
        feeRateBps,
        applicationFeeWaived: expectBoolean(record.applicationFeeWaived, `${path}.applicationFeeWaived`),
        commercialPolicyVersion: expectString(record.commercialPolicyVersion, `${path}.commercialPolicyVersion`),
        pricingCatalogVersion: record.pricingCatalogVersion === null
            ? null
            : parseStudentBillingIdentifier(record.pricingCatalogVersion, `${path}.pricingCatalogVersion`)
    };
}
export function parseFinalizeStudentBillingMonthlyUnitsRequest(value, path = "studentBillingMonthlyFinalizationRequest") {
    const record = expectRecord(value, path);
    exact(record, ["expectedGenerationVersion", "units"], path);
    const units = expectArray(record.units, (item, itemPath) => {
        const unit = expectRecord(item, itemPath);
        exact(unit, ["billingUnitId", "expectedDraftVersion"], itemPath);
        return {
            billingUnitId: parseStudentBillingIdentifier(unit.billingUnitId, `${itemPath}.billingUnitId`),
            expectedDraftVersion: integer(unit.expectedDraftVersion, `${itemPath}.expectedDraftVersion`, 1)
        };
    }, `${path}.units`);
    if (units.length === 0 || units.length > 150 || new Set(units.map((unit) => unit.billingUnitId)).size !== units.length) {
        throw new Error(`${path}.units is invalid`);
    }
    return { expectedGenerationVersion: integer(record.expectedGenerationVersion, `${path}.expectedGenerationVersion`, 1), units };
}
export function parseUpdateStudentBillingTenantCollectionRequest(value, path = "studentBillingTenantCollectionRequest") {
    const record = expectRecord(value, path);
    exact(record, ["status", "expectedVersion"], path);
    return {
        status: enumValue(record.status, ["issued", "collected"], `${path}.status`),
        expectedVersion: integer(record.expectedVersion, `${path}.expectedVersion`, 1)
    };
}
export function parseFinalizedStudentBillingPayerSnapshot(value, path = "finalizedPayerSnapshot") {
    const record = expectRecord(value, path);
    exact(record, ["payerId", "payerAssignmentId", "payerType", "safeDisplayLabel", "assignmentEffectiveFrom", "assignmentEffectiveTo"], path);
    return {
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        payerAssignmentId: parseStudentBillingIdentifier(record.payerAssignmentId, `${path}.payerAssignmentId`),
        payerType: enumValue(record.payerType, ["platformUser", "externalContact"], `${path}.payerType`),
        safeDisplayLabel: expectString(record.safeDisplayLabel, `${path}.safeDisplayLabel`),
        assignmentEffectiveFrom: parseStudentBillingDate(record.assignmentEffectiveFrom, `${path}.assignmentEffectiveFrom`),
        assignmentEffectiveTo: record.assignmentEffectiveTo === null ? null : parseStudentBillingDate(record.assignmentEffectiveTo, `${path}.assignmentEffectiveTo`)
    };
}
export function parseStudentBillingMonthlyFinalizedUnit(value, path = "studentBillingMonthlyFinalizedUnit") {
    const record = expectRecord(value, path);
    exact(record, [
        "billingUnitId", "tenantId", "studentId", "studentDisplayNameSnapshot", "serviceMonth", "finalizationStatus",
        "currency", "lines", "lessonSubtotal", "eventSubtotal", "manualSubtotal", "calculatedTotal", "finalAmount",
        "sourceFingerprint", "draftVersionAtFinalization", "generationVersionAtFinalization", "payerSnapshot",
        "collectionMethod", "collectionMethodSource", "commercialSnapshot", "cardFeePolicySnapshot", "scheduledChargeDate",
        "finalizedByPlatformUserId", "finalizedAt", "version"
    ], path);
    if (record.finalizationStatus !== "finalized" || record.version !== 1)
        throw new Error(`${path}.finalizationStatus is invalid`);
    const lines = expectArray(record.lines, parseStudentBillingMonthlyLine, `${path}.lines`);
    if (lines.length > 100)
        throw new Error(`${path}.lines exceeds limit`);
    const lessonSubtotal = integer(record.lessonSubtotal, `${path}.lessonSubtotal`);
    const eventSubtotal = integer(record.eventSubtotal, `${path}.eventSubtotal`);
    const manualSubtotal = integer(record.manualSubtotal, `${path}.manualSubtotal`);
    const calculatedTotal = integer(record.calculatedTotal, `${path}.calculatedTotal`);
    const finalAmount = integer(record.finalAmount, `${path}.finalAmount`);
    if (lessonSubtotal + eventSubtotal + manualSubtotal !== calculatedTotal || finalAmount !== calculatedTotal || lines.reduce((sum, line) => sum + line.amount, 0) !== calculatedTotal) {
        throw new Error(`${path}.totals are invalid`);
    }
    const commercial = expectRecord(record.commercialSnapshot, `${path}.commercialSnapshot`);
    exact(commercial, ["contractPricingMode", "commercialStateVersion"], `${path}.commercialSnapshot`);
    const payerSnapshot = record.payerSnapshot === null
        ? null
        : parseFinalizedStudentBillingPayerSnapshot(record.payerSnapshot, `${path}.payerSnapshot`);
    const collectionMethod = record.collectionMethod === null ? null : enumValue(record.collectionMethod, STUDENT_BILLING_COLLECTION_METHODS, `${path}.collectionMethod`);
    const collectionMethodSource = record.collectionMethodSource === null ? null : enumValue(record.collectionMethodSource, STUDENT_BILLING_COLLECTION_SOURCES, `${path}.collectionMethodSource`);
    const scheduledChargeDate = record.scheduledChargeDate === null
        ? null
        : parseStudentBillingDate(record.scheduledChargeDate, `${path}.scheduledChargeDate`);
    // cardFeePolicySnapshot was introduced prospectively. Missing legacy data is
    // intentionally readable, but is never eligible for customer-invoice preparation.
    const cardFeePolicySnapshot = record.cardFeePolicySnapshot === null || record.cardFeePolicySnapshot === undefined
        ? null
        : parseFinalizedStudentBillingCardFeePolicySnapshot(record.cardFeePolicySnapshot, `${path}.cardFeePolicySnapshot`);
    if (finalAmount === 0 && (payerSnapshot !== null || collectionMethod !== null || collectionMethodSource !== null || scheduledChargeDate !== null)) {
        throw new Error(`${path}.zero amount authority is invalid`);
    }
    if (finalAmount > 0 && (payerSnapshot === null || collectionMethod === null || collectionMethodSource === null)) {
        throw new Error(`${path}.positive amount authority is invalid`);
    }
    if (finalAmount > 0 && collectionMethod === "card" && scheduledChargeDate === null) {
        throw new Error(`${path}.card schedule is invalid`);
    }
    if (collectionMethod !== "card" && scheduledChargeDate !== null) {
        throw new Error(`${path}.non-card schedule is invalid`);
    }
    if (collectionMethod !== "card" && cardFeePolicySnapshot !== null) {
        throw new Error(`${path}.non-card fee policy is invalid`);
    }
    return {
        billingUnitId: parseStudentBillingIdentifier(record.billingUnitId, `${path}.billingUnitId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        studentDisplayNameSnapshot: expectString(record.studentDisplayNameSnapshot, `${path}.studentDisplayNameSnapshot`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        finalizationStatus: "finalized",
        currency: parseStudentBillingCurrency(record.currency, `${path}.currency`),
        lines,
        lessonSubtotal,
        eventSubtotal,
        manualSubtotal,
        calculatedTotal,
        finalAmount,
        sourceFingerprint: fingerprint(record.sourceFingerprint, `${path}.sourceFingerprint`),
        draftVersionAtFinalization: integer(record.draftVersionAtFinalization, `${path}.draftVersionAtFinalization`, 1),
        generationVersionAtFinalization: integer(record.generationVersionAtFinalization, `${path}.generationVersionAtFinalization`, 1),
        payerSnapshot,
        collectionMethod,
        collectionMethodSource,
        commercialSnapshot: {
            contractPricingMode: enumValue(commercial.contractPricingMode, ["paymentProcessing", "management"], `${path}.commercialSnapshot.contractPricingMode`),
            commercialStateVersion: integer(commercial.commercialStateVersion, `${path}.commercialSnapshot.commercialStateVersion`, 1)
        },
        cardFeePolicySnapshot,
        scheduledChargeDate,
        finalizedByPlatformUserId: parseStudentBillingIdentifier(record.finalizedByPlatformUserId, `${path}.finalizedByPlatformUserId`),
        finalizedAt: iso(record.finalizedAt, `${path}.finalizedAt`),
        version: 1
    };
}
export function parseStudentBillingTenantCollectionState(value, path = "studentBillingTenantCollectionState") {
    const record = expectRecord(value, path);
    exact(record, ["billingUnitId", "tenantId", "studentId", "serviceMonth", "collectionMethod", "status", "version", "updatedByPlatformUserId", "updatedAt"], path);
    if (record.collectionMethod !== "tenantCollection")
        throw new Error(`${path}.collectionMethod is invalid`);
    return {
        billingUnitId: parseStudentBillingIdentifier(record.billingUnitId, `${path}.billingUnitId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth, `${path}.serviceMonth`),
        collectionMethod: "tenantCollection",
        status: enumValue(record.status, STUDENT_BILLING_TENANT_COLLECTION_STATUSES, `${path}.status`),
        version: integer(record.version, `${path}.version`, 1),
        updatedByPlatformUserId: parseStudentBillingIdentifier(record.updatedByPlatformUserId, `${path}.updatedByPlatformUserId`),
        updatedAt: iso(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingMonthlyViewUnit(value, path = "studentBillingMonthlyViewUnit") {
    const record = expectRecord(value, path);
    if (record.finalizationStatus === "draft") {
        exact(record, [
            "billingUnitId", "tenantId", "studentId", "studentDisplayNameSnapshot", "serviceMonth", "finalizationStatus", "readiness",
            "currency", "lines", "lessonSubtotal", "eventSubtotal", "manualSubtotal", "totalAmount", "warnings", "payerPreview",
            "sourceFingerprint", "version", "createdAt", "updatedAt", "canFinalize"
        ], path);
        const { canFinalize, ...draft } = record;
        const parsed = parseStudentBillingMonthlyUnit(draft, path);
        return { ...parsed, canFinalize: expectBoolean(canFinalize, `${path}.canFinalize`) };
    }
    exact(record, [
        "billingUnitId", "tenantId", "studentId", "studentDisplayNameSnapshot", "serviceMonth", "finalizationStatus",
        "currency", "lines", "lessonSubtotal", "eventSubtotal", "manualSubtotal", "calculatedTotal", "finalAmount",
        "sourceFingerprint", "draftVersionAtFinalization", "generationVersionAtFinalization", "payerSnapshot", "collectionMethod",
        "collectionMethodSource", "commercialSnapshot", "scheduledChargeDate", "finalizedByPlatformUserId", "finalizedAt", "version",
        "cardFeePolicySnapshot", "canFinalize", "tenantCollectionState", "cardPaymentStatus", "cardReadiness",
        "feePolicyReady", "cardInvoiceBlockingReason"
    ], path);
    if (record.canFinalize !== false)
        throw new Error(`${path}.canFinalize is invalid`);
    const { canFinalize: _canFinalize, tenantCollectionState, cardPaymentStatus, cardReadiness, feePolicyReady, cardInvoiceBlockingReason, ...finalized } = record;
    const parsed = parseStudentBillingMonthlyFinalizedUnit(finalized, path);
    if (cardPaymentStatus !== null && !["notStarted", "paymentPending", "processing", "paid", "failed", "needsAttention"].includes(String(cardPaymentStatus)))
        throw new Error(`${path}.cardPaymentStatus is invalid`);
    const readiness = cardReadiness === null ? null : expectRecord(cardReadiness, `${path}.cardReadiness`);
    if (readiness)
        exact(readiness, ["paymentMethodReady", "paymentAccountReady"], `${path}.cardReadiness`);
    return {
        ...parsed,
        canFinalize: false,
        tenantCollectionState: tenantCollectionState === null ? null : parseStudentBillingTenantCollectionState(tenantCollectionState, `${path}.tenantCollectionState`),
        cardPaymentStatus: cardPaymentStatus,
        cardReadiness: readiness ? {
            paymentMethodReady: expectBoolean(readiness.paymentMethodReady, `${path}.cardReadiness.paymentMethodReady`),
            paymentAccountReady: expectBoolean(readiness.paymentAccountReady, `${path}.cardReadiness.paymentAccountReady`)
        } : null,
        feePolicyReady: expectBoolean(feePolicyReady, `${path}.feePolicyReady`),
        cardInvoiceBlockingReason: cardInvoiceBlockingReason === null
            ? null
            : enumValue(cardInvoiceBlockingReason, ["feePolicyNotReady"], `${path}.cardInvoiceBlockingReason`)
    };
}
export function studentBillingMonthlyDraftCanFinalize(input) {
    return !input.stale
        && input.unit.totalAmount >= 0
        && !input.globalWarnings.some((warning) => warning.blocking)
        && !input.unit.warnings.some((warning) => warning.blocking);
}
